import { z } from 'zod';

export const LogicalModelSchema = z.enum(['HEAVY', 'ROUTINE', 'CHEAP', 'INTAKE']);
export type LogicalModel = z.infer<typeof LogicalModelSchema>;

export const TopicIdeaSchema = z.object({
  slotDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  title: z.string().min(2).max(280),
  category: z.string().min(1).max(40),
  brandOrSales: z.enum(['brand', 'sales']),
  sourceEvent: z.string().max(280).nullable().optional(),
  rationale: z.string().max(2000).nullable().optional(),
  sceneTags: z.array(z.string().min(1).max(40)).max(10).default([]),
});
export type TopicIdea = z.infer<typeof TopicIdeaSchema>;

export const TopicIdeationOutputSchema = z.object({
  topics: z.array(TopicIdeaSchema).min(1).max(60),
});
export type TopicIdeationOutput = z.infer<typeof TopicIdeationOutputSchema>;

export const CopyOutputSchema = z.object({
  body: z.string().min(1).max(8000),
  hashtags: z.array(z.string().min(1).max(60)).max(50).default([]),
  sourceUrl: z.string().url().nullable().optional(),
  sceneTags: z.array(z.string().min(1).max(40)).max(10).default([]),
});
export type CopyOutput = z.infer<typeof CopyOutputSchema>;

/**
 * A list the model fills in, normalised rather than validated.
 *
 * Intake reads a whole website and answers with several lists at once. A single
 * badly shaped entry used to reject the entire answer — a live Sonnet 4.6 run on
 * the 25-page hotelpalota.hu crawl (2026-08-06) returned 13 amenities written as
 * full sentences, and the crawl plus the model call, some 90 seconds of work,
 * were thrown away over list formatting. Entries that do not fit the field are
 * dropped instead: prose belongs in the USP list, and from phase 2 in the fact
 * records, which carry provenance. Only a structurally broken answer (no brand
 * voice, wrong types on the object itself) is still an error.
 */
function modelList(minLen: number, maxLen: number, maxItems: number) {
  return z
    .array(z.unknown())
    .default([])
    .transform((list) =>
      list
        .filter((v): v is string => typeof v === 'string')
        .map((s) => s.trim())
        .filter((s) => s.length >= minLen && s.length <= maxLen)
        .slice(0, maxItems),
    );
}

const BrandVoiceSuggestionSchema = z.object({
  tone: z.enum(['warm', 'elegant', 'playful']).catch('warm').default('warm'),
  emojiDensity: z.enum(['high', 'medium', 'low']).catch('medium').default('medium'),
  signaturePhrases: modelList(1, 120, 20),
  avoidWords: modelList(1, 60, 40),
  ctaStyle: z.string().min(1).max(120).catch('invitation').default('invitation'),
});

/**
 * The brand voice, however the model chose to phrase it.
 *
 * Three consecutive live runs on 2026-08-06 each failed on a different shape
 * quibble, the last one a `brandVoice` returned as the bare string "warm".
 * A voice suggestion is the cheapest part of the answer — the manager overrides
 * it in the UI — so an unusable one falls back to the default rather than
 * discarding the extracted facts, which cost a crawl and a model call, with it.
 */
const BrandVoiceInputSchema = z.preprocess((raw) => {
  if (typeof raw === 'string') return { tone: raw };
  if (raw === null || raw === undefined) return {};
  return raw;
}, BrandVoiceSuggestionSchema);

/** `"0.8"` and 42 are both things models answer; neither should end the run. */
const ConfidenceSchema = z.coerce.number().min(0).max(1).catch(0.5).default(0.5);

export const AiIntakeOutputSchema = z.object({
  usp: modelList(2, 280, 20),
  amenities: modelList(1, 60, 60),
  locationKeywords: modelList(1, 60, 40),
  brandVoice: BrandVoiceInputSchema,
  confidence: ConfidenceSchema,
});
export type AiIntakeOutput = z.infer<typeof AiIntakeOutputSchema>;
